import { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Menu from 'components/shared/menu';
import {
  HighCostMedicationListItem,
  RxnormCoding,
  RxnormState,
  RxnormStateCounts,
} from 'models/HighCostMedicationListItem';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import InfoIconOutline from 'svg/InfoIconOutline';

export type RxnormFilter = 'all' | 'flagged';

export type RxnormNameField = 'genericName' | 'brandName';

export type RxnormRename = (item: HighCostMedicationListItem, field: RxnormNameField, name: string) => void;

// The list endpoints' rxnormState filter values for each view. Flagged is the rows referrals won't be
// checked for.
export const RXNORM_FILTER_STATES: Record<RxnormFilter, RxnormState[] | undefined> = {
  all: undefined,
  flagged: ['not_recognized'],
};

export const NEEDS_REVIEW_STATES: RxnormState[] = [
  'brand_not_recognized',
  'generic_not_recognized',
  'generic_brand_conflict',
];

// Snake_case keys are the contract, but both api paths that carry these counts (OliveBranch on HTTP,
// Broadcasters::Base on ActionCable) deep-camelize hash keys, so either spelling may arrive.
export function rxnormStateCount(counts: RxnormStateCounts, state: keyof RxnormStateCounts) {
  const camel = state.replace(/_(\w)/g, (_, c: string) => c.toUpperCase());
  const byKey = counts as Record<string, number | undefined>;

  return byKey[state] ?? byKey[camel] ?? 0;
}

// The brand disambiguates two rows sharing a generic at different tiers.
export const medicationLabel = (item: HighCostMedicationListItem) =>
  item.brandName ? `${item.genericName} (${item.brandName})` : item.genericName;

export const ingredientsLabel = (reading: { name: string }[]) => reading.map((concept) => concept.name).join(' / ');

// Each reading is a list of base ingredients; a cell like `ALBUKED or ALBUTEIN` has several.
export const matchedAsLabel = (matchedAs: { name: string }[][]) => matchedAs.map(ingredientsLabel).join(' or ');

export const isFlagged = (item: HighCostMedicationListItem) => item.rxnorm?.state === 'not_recognized';

const flaggedRowStyle: CSSProperties = { backgroundColor: colors.orange10 };

export const rxnormRowStyle = (row: { original: HighCostMedicationListItem }) =>
  isFlagged(row.original) ? flaggedRowStyle : undefined;

// The outlined info glyph turned over is the outlined exclamation the design uses.
export const ExclamationIcon = ({ color }: { color: string }) => (
  <InfoIconOutline width={14} height={14} fill={color} style={{ transform: 'rotate(180deg)', flexShrink: 0 }} />
);

type Suggestion = { field: RxnormNameField; name: string; rxcui: string };

const suggestionsFor = (rxnorm: RxnormCoding): Suggestion[] =>
  [
    ...(rxnorm.generic?.suggestions ?? []).map((s) => ({ ...s, field: 'genericName' as const })),
    ...(rxnorm.brand?.suggestions ?? []).map((s) => ({ ...s, field: 'brandName' as const })),
  ].slice(0, 3);

export function SuggestedNames({ children }: { children: ReactNode }) {
  return (
    <SuggestedNamesBlock>
      <BodySmallBold>Suggested Name(s)</BodySmallBold>
      <Chips>{children}</Chips>
    </SuggestedNamesBlock>
  );
}

export function SuggestionChip({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <Button type='button' variant='outline-primary' size='xsmall' onClick={onClick}>
      {name}
    </Button>
  );
}

// The Medication cell: the name, then the row's RxNorm state beneath it when it needs saying. A
// recognized row shows the name alone. An absent rxnorm object (api not yet shipped) does too.
export function MedicationCell({ item, onRename }: { item: HighCostMedicationListItem; onRename?: RxnormRename }) {
  const { rxnorm } = item;
  const label = medicationLabel(item);

  if (!rxnorm || rxnorm.state === 'checked') return <BodySmallBold>{label}</BodySmallBold>;

  if (rxnorm.state === null) {
    return (
      <Cell>
        <BodySmallBold>{label}</BodySmallBold>
        <Detail>Not checked yet</Detail>
      </Cell>
    );
  }

  if (rxnorm.state === 'not_recognized') {
    const suggestions = suggestionsFor(rxnorm);

    return (
      <Cell data-testid='rxnorm-flagged'>
        <Title>
          <ExclamationIcon color={colors.orangeExtraDark} />
          <FlaggedName>{label}</FlaggedName>
        </Title>
        <FlaggedDetail>
          Olio couldn&rsquo;t validate this medication, so referrals won&rsquo;t be checked for it.
          {suggestions.length > 0 && (
            <>
              <br />
              Did you mean{' '}
              {onRename ? (
                <SuggestionsPopover item={item} label={label} suggestions={suggestions} onRename={onRename} />
              ) : (
                suggestions[0].name
              )}
              ?
            </>
          )}
        </FlaggedDetail>
      </Cell>
    );
  }

  const detail = reviewDetail(item, rxnorm);
  if (!detail) return <BodySmallBold>{label}</BodySmallBold>;

  return (
    <Cell data-testid='rxnorm-needs-review'>
      <Title>
        <InfoIconOutline width={14} height={14} fill={colors.black100} style={{ flexShrink: 0 }} />
        <BodySmallBold>{label}</BodySmallBold>
      </Title>
      <Detail>{detail}</Detail>
    </Cell>
  );
}

function reviewDetail(item: HighCostMedicationListItem, rxnorm: RxnormCoding): ReactNode {
  switch (rxnorm.state) {
    case 'brand_not_recognized':
      return (
        <>
          Olio couldn&rsquo;t validate the brand name <Strong>{item.brandName}</Strong>. It will still be checked by its
          generic name.
        </>
      );
    case 'generic_not_recognized':
      return (
        <>
          Olio couldn&rsquo;t validate the generic name <Strong>{item.genericName}</Strong>. It will still be checked by
          its brand name.
        </>
      );
    case 'generic_brand_conflict':
      return (
        <>
          Generic and brand name point to different medications:{' '}
          <Strong>{matchedAsLabel(rxnorm.generic?.matchedAs ?? [])}</Strong> vs.{' '}
          <Strong>{matchedAsLabel(rxnorm.brand?.matchedAs ?? [])}</Strong>. Referrals mentioning either medication will
          be flagged.
        </>
      );
    default:
      // A state this bundle does not know yet renders as nothing rather than as a guess.
      return null;
  }
}

type PopoverProps = {
  item: HighCostMedicationListItem;
  label: string;
  suggestions: Suggestion[];
  onRename: RxnormRename;
};

// Choosing a suggestion renames the row straight away, replacing whichever name it was suggested for.
function SuggestionsPopover({ item, label, suggestions, onRename }: PopoverProps) {
  return (
    <Menu>
      <Menu.Trigger>
        <SuggestionLink type='button'>{suggestions[0].name}</SuggestionLink>
      </Menu.Trigger>
      <Menu.Content position='center' style={{ maxWidth: 280, padding: 16 }}>
        <Popover>
          <BodySmallBold>Olio couldn&rsquo;t validate &lsquo;{label}&rsquo;</BodySmallBold>
          <BodySmall>
            This medication requires a matching name with RxNorm (Olio&rsquo;s validation provider) to be checked.
          </BodySmall>
          <SuggestedNames>
            {suggestions.map((suggestion) => (
              <SuggestionChip
                key={`${suggestion.field}-${suggestion.rxcui}`}
                name={suggestion.name}
                onClick={() => onRename(item, suggestion.field, suggestion.name)}
              />
            ))}
          </SuggestedNames>
        </Popover>
      </Menu.Content>
    </Menu>
  );
}

type FlaggedCountProps = {
  counts?: RxnormStateCounts;
  value: RxnormFilter;
  onChange: (value: RxnormFilter) => void;
};

// Follows the medication count. Counts cover the whole list whatever the search, filter or page, and
// both list queries keep their previous data while the next loads, so the link holds steady while
// the table changes beneath it.
export function FlaggedCount({ counts, value, onChange }: FlaggedCountProps) {
  const flagged = counts ? rxnormStateCount(counts, 'not_recognized') : 0;
  if (value === 'all' && flagged === 0) return null;

  return (
    <>
      <Dot aria-hidden>&bull;</Dot>
      <FlaggedLink type='button' aria-pressed={value === 'flagged'} onClick={() => onChange('flagged')}>
        {flagged} Flagged
      </FlaggedLink>
      {value === 'flagged' && (
        <ShowAllLink type='button' onClick={() => onChange('all')}>
          Show all
        </ShowAllLink>
      )}
    </>
  );
}

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FlaggedName = styled(BodySmallBold)`
  color: ${colors.orangeExtraDark};
`;

const Detail = styled.span`
  font-size: 12px;
  line-height: 1.6;
  color: ${colors.black75};
`;

const FlaggedDetail = styled(Detail)`
  color: ${colors.orangeExtraDark};
`;

const linkReset = `
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  cursor: pointer;
`;

const SuggestionLink = styled.button`
  ${linkReset}
  color: ${colors.blue100};
  font-weight: 500;
  text-decoration: underline;
`;

const Popover = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SuggestedNamesBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Dot = styled.span`
  color: ${colors.black25};
`;

const FlaggedLink = styled.button`
  ${linkReset}
  color: ${colors.orangeHover};
`;

const ShowAllLink = styled.button`
  ${linkReset}
  color: ${colors.blue100};
`;

const Strong = styled.span`
  font-weight: 500;
`;
